import { Crown, Settings, Trash2, UserX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import type { Household, HouseholdInvite, HouseholdMember } from '@/types/household'
import { HouseholdInviteForm } from './household-invite-form'
import { HouseholdInvitesList } from './household-invites-list'
import { HouseholdMembersList } from './household-members-list'

interface HouseholdCardProps {
  household: Household
  currentHouseholdId?: string
  isEditing: boolean
  editName: string
  loading: boolean
  isAdmin: boolean
  currentUserRole: string | null
  memberCount: number
  members: HouseholdMember[]
  invites: HouseholdInvite[]
  currentUserId?: string
  currentUserDisplayName?: string
  showInviteForm: boolean
  inviteEmail: string
  inviteRole: 'admin' | 'member'
  inviteSubmitting: boolean
  copiedInviteId: string | null
  onEditNameChange: (value: string) => void
  onStartEditing: () => void
  onCancelEditing: () => void
  onSaveEditing: () => void
  onSelectHousehold: () => void
  onToggleInviteForm: () => void
  onInviteEmailChange: (value: string) => void
  onInviteRoleChange: (role: 'admin' | 'member') => void
  onCreateInvite: () => void
  onCopyInviteLink: (inviteCode: string) => void
  onDeleteInvite: (inviteId: string) => void
  onRemoveMember: (memberId: string) => void
  onDeleteHousehold: () => void
  onLeaveHousehold: () => void
}

export function HouseholdCard({
  household,
  currentHouseholdId,
  isEditing,
  editName,
  loading,
  isAdmin,
  currentUserRole,
  memberCount,
  members,
  invites,
  currentUserId,
  currentUserDisplayName,
  showInviteForm,
  inviteEmail,
  inviteRole,
  inviteSubmitting,
  copiedInviteId,
  onEditNameChange,
  onStartEditing,
  onCancelEditing,
  onSaveEditing,
  onSelectHousehold,
  onToggleInviteForm,
  onInviteEmailChange,
  onInviteRoleChange,
  onCreateInvite,
  onCopyInviteLink,
  onDeleteInvite,
  onRemoveMember,
  onDeleteHousehold,
  onLeaveHousehold,
}: HouseholdCardProps) {
  const isActive = currentHouseholdId === household.id

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 flex items-center justify-between">
        {isEditing ? (
          <div className="flex flex-1 gap-2">
            <Input
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSaveEditing()}
              className="flex-1"
            />
            <Button size="sm" onClick={onSaveEditing} disabled={loading || !editName.trim()}>
              Opslaan
            </Button>
            <Button variant="outline" size="sm" onClick={onCancelEditing}>
              Annuleren
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{household.name}</h3>
              {currentUserRole === 'admin' && (
                <Badge variant="secondary" className="text-xs">
                  <Crown className="mr-1 h-3 w-3" />
                  Admin
                </Badge>
              )}
              {isActive && <Badge className="text-xs">Actief</Badge>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onSelectHousehold}>
                {isActive ? 'Actief' : 'Selecteren'}
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={onStartEditing}>
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="text-sm text-gray-600">
        {memberCount} {memberCount === 1 ? 'lid' : 'leden'}
      </div>

      {isActive && (
        <div className="mt-4 space-y-4 border-t pt-4">
          {isAdmin && (
            <HouseholdInviteForm
              showInviteForm={showInviteForm}
              inviteEmail={inviteEmail}
              inviteRole={inviteRole}
              inviteSubmitting={inviteSubmitting}
              onToggle={onToggleInviteForm}
              onEmailChange={onInviteEmailChange}
              onRoleChange={onInviteRoleChange}
              onSubmit={onCreateInvite}
            />
          )}

          {isAdmin && invites.length > 0 && (
            <HouseholdInvitesList
              invites={invites}
              copiedInviteId={copiedInviteId}
              onCopyInviteLink={onCopyInviteLink}
              onDeleteInvite={onDeleteInvite}
            />
          )}

          <HouseholdMembersList
            members={members}
            currentUserId={currentUserId}
            currentUserDisplayName={currentUserDisplayName}
            isAdmin={isAdmin}
            onRemoveMember={onRemoveMember}
          />

          <div className="border-t pt-4">
            {isAdmin ? (
              <Button
                variant="outline"
                onClick={onDeleteHousehold}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Huishouden Verwijderen
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onLeaveHousehold}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <UserX className="mr-2 h-4 w-4" />
                Huishouden Verlaten
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
